import React, { useEffect, useRef, useState } from "react";
import { Loader2, MonitorUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { RtcRoom } from "../../lib/rtc";

// Rep side: answers an admin's request to watch their screen. Mounted in the rep portal and call center.
export function RepScreenShare() {
  const [req, setReq] = useState(null);
  const [live, setLive] = useState(false);
  const room = useRef(null);
  useEffect(() => {
    let on = true;
    const poll = () => api.get("/rep/screen-request").then((r) => on && setReq(r.data.status === "none" ? null : r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    return () => { on = false; clearInterval(t); room.current?.stop(); };
  }, []);
  useEffect(() => { if (live && !req) { room.current?.stop(); room.current = null; setLive(false); } }, [req, live]);
  if (!req) return null;
  const stop = async () => { room.current?.stop(); room.current = null; setLive(false); await api.post(`/rep/screen-request/${req.req_id}/stop`).catch(() => {}); setReq(null); };
  const accept = async () => {
    let s;
    try { s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 10 }, audio: false }); } catch { return toast.error("Screen sharing was cancelled"); }
    try {
      const { data } = await api.post(`/rep/screen-request/${req.req_id}/accept`);
      const r = new RtcRoom({ ...data.join, stage: true, host: true }, () => {});
      r.local = s;
      s.getVideoTracks()[0].onended = stop;
      room.current = r;
      await r.start();
      setLive(true);
    } catch (e) { s.getTracks().forEach((t) => t.stop()); toast.error(formatApiError(e)); }
  };
  const decline = async () => { await api.post(`/rep/screen-request/${req.req_id}/decline`).catch(() => {}); setReq(null); };
  return (
    <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[60] bg-[#1c1917] text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="rep-screen-banner">
      <span className={`w-2.5 h-2.5 rounded-full ${live ? "bg-red-500 animate-pulse" : "bg-[var(--gold)]"}`} />
      {live ? <span data-testid="rep-screen-live">Sharing your screen with {req.admin_name}</span> : <span>{req.admin_name} wants to see your screen</span>}
      {live ? <button onClick={stop} className="rounded-full bg-white/15 px-3 py-1 font-semibold" data-testid="rep-screen-stop">Stop sharing</button>
        : <><button onClick={accept} className="rounded-full bg-[var(--gold)] text-[#1c1917] px-3 py-1 font-semibold" data-testid="rep-screen-accept">Share screen</button>
          <button onClick={decline} className="rounded-full bg-white/15 px-3 py-1" data-testid="rep-screen-decline">Not now</button></>}
    </div>
  );
}

// Admin side: asks a rep to share and shows their screen live.
export function AdminScreenViewer({ rep, onClose }) {
  const [st, setSt] = useState({ status: "asking" });
  const [stream, setStream] = useState(null);
  const vid = useRef(null);
  const room = useRef(null);
  useEffect(() => {
    let on = true;
    let t;
    let started = false;
    const tick = async () => {
      const { data } = await api.get(`/admin/reps/${rep.user_id}/screen`).catch(() => ({ data: { status: "none" } }));
      if (!on) return;
      setSt(data);
      if (data.status === "live" && !room.current) {
        const r = new RtcRoom({ ...data.join, stage: false, host: false }, (s) => { const p = s.peers.find((x) => x.stream); setStream(p ? p.stream : null); if (["ended", "disconnected"].includes(s.status)) setSt({ status: "ended" }); });
        room.current = r;
        r.start();
      }
      if (["declined", "none", "ended"].includes(data.status)) { room.current?.stop(); room.current = null; setStream(null); }
    };
    const boot = setTimeout(() => {
      started = true;
      api.post(`/admin/reps/${rep.user_id}/screen-request`).then(tick).catch((e) => setSt({ status: "error", detail: formatApiError(e) }));
      t = setInterval(tick, 3000);
    }, 60);
    return () => { on = false; clearTimeout(boot); clearInterval(t); room.current?.stop(); if (started) api.post(`/admin/reps/${rep.user_id}/screen-stop`).catch(() => {}); };
  }, [rep.user_id]);
  useEffect(() => { if (vid.current) vid.current.srcObject = stream; }, [stream]);
  const msg = { asking: "Asking…", pending: `Waiting for ${rep.name} to tap "Share screen". They must have the Rep Portal or Call Center open.`, declined: `${rep.name} said not now.`, none: "The share ended.", ended: "The share ended.", error: st.detail };
  return (
    <div className="fixed inset-0 z-[200] bg-[#0b0a09] flex flex-col p-4 md:p-8" data-testid="admin-screen-viewer">
      <div className="flex items-center gap-3 text-white mb-3"><MonitorUp className="w-5 h-5 text-[var(--gold)]" /><p className="font-serif text-xl flex-1">{rep.name}'s screen</p>
        <button onClick={onClose} className="rounded-full bg-white/15 p-2" aria-label="Close" data-testid="admin-screen-close"><X className="w-5 h-5" /></button></div>
      <div className="flex-1 rounded-2xl bg-[#111] overflow-hidden flex items-center justify-center">
        {stream ? <video ref={vid} autoPlay playsInline muted className="w-full h-full object-contain" data-testid="admin-screen-video" />
          : <p className="text-white/70 text-sm flex items-center gap-2 px-6 text-center" data-testid="admin-screen-status">{["asking", "pending", "live"].includes(st.status) && <Loader2 className="w-4 h-4 animate-spin" />}{st.status === "live" ? "Connecting…" : msg[st.status] || ""}</p>}
      </div>
    </div>
  );
}
